// Client/src/Error/axiosInterceptor.ts

import { isAbortError } from "../utils/errorMessage";
import { isHandledDmArticleRefreshNotFound } from "./fetchErrorPolicy";

let originalFetch: typeof fetch;

/*
  ---------------------------------------------------
  RequestDetails Interface
  ---------------------------------------------------
*/
interface RequestDetails {
  url: string;
  method: string;
  body: unknown; // Kann angepasst werden, je nachdem, wie der Body behandelt werden soll
  response?: {
    status: number;
    statusText: string;
  };
  isOffline?: boolean; // Neues Flag für Offline-Status
}

/*
  ---------------------------------------------------
  setupFetchInterceptor Funktion
  ---------------------------------------------------
*/
export const setupFetchInterceptor = (
  setError: (error: Error, errorInfo?: React.ErrorInfo) => void,
  setRequestDetails: (details: RequestDetails | null) => void,
  setServerErrorDetails: (details: unknown | null) => void
) => {

  // Nur einmal überschreiben
  if (!originalFetch) {
    originalFetch = window.fetch;
  }

   window.fetch = async (...args) => {
    const [input, init] = args;
    // Korrigiert: URL-Extraktion aus verschiedenen Input-Typen
    const url = typeof input === 'string' 
      ? input 
      : input instanceof URL 
        ? input.toString() 
        : input instanceof Request 
          ? input.url 
          : '';
    const method = init?.method ? init.method.toUpperCase() : 'GET';
    let body: unknown = null;

    if (init?.body) {
      try {
        // Versuche, den Body als JSON zu parsen, falls er ein String ist
        if (typeof init.body === 'string') {
          body = JSON.parse(init.body);
        } else if (init.body instanceof URLSearchParams) {
          body = Object.fromEntries(init.body.entries());
        } else if (init.body instanceof FormData) {
          const formBody: Record<string, FormDataEntryValue> = {};
          init.body.forEach((value, key) => {
            formBody[key] = value;
          });
          body = formBody;
        } else {
          body = init.body;
        }
      } catch {
        body = init.body;
      }
    }

    try {
      const response = await originalFetch(...args);

      // HTTP-Fehler (z.B. 404, 500)
      if (!response.ok) {
        // Fange den Fehler und setze ihn als State unauthentifiziert
        if (response.status === 401) {
          return response;
        } else if (response.status === 423) {
          return response;
        } else if (
          response.status === 409 &&
          url.includes('/api/shelf-layouts/') &&
          url.includes('/check-availability')
        ) {
          return response;
        } else if (
          response.status === 409 &&
          url.includes('/api/shelf-actions/') &&
          url.includes('/subscriptions')
        ) {
          return response;
        } else if (
          response.status === 409 &&
          method === 'DELETE' &&
          (url.includes('/api/shelf-layouts/') || url.includes('/api/shelf-templates/'))
        ) {
          // Fachlicher Löschkonflikt: Die jeweilige Seite zeigt die konkrete
          // Aktivierung bzw. Marktnutzung an. Kein globales Issue-Overlay öffnen.
          return response;
        } else if (isHandledDmArticleRefreshNotFound(url, method, response.status)) {
          // Der Refresh-Aufrufer zeigt die fachliche Meldung und den weiterhin
          // verfügbaren Cache-Eintrag selbst an. Kein globales Bug-Overlay öffnen.
          return response;
        } else if (response.status === 404) {
          // Erlaube 404-Fehler für die Store-Validierung
          // Suche nach /api/stores/validate im URL-Pfad
          if (url.includes('/api/stores/validate')) {
            return response;
          }
        } else if (
          response.status === 400 &&
          url.includes('/api/users/me/layout-designer-store')
        ) {
          return response;
        } else if (
          (response.status === 400 || response.status === 409) &&
          (url.includes('/api/users/employee-id/') || (url.endsWith('/api/users') && method === 'POST'))
        ) {
          return response;
        }
        
        let serverErrorDetails: unknown | null = null;
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const payload = await response.clone().json().catch(() => null);
          if (payload && typeof payload === 'object' && 'server_error_details' in payload) {
            serverErrorDetails = (payload as { server_error_details?: unknown }).server_error_details ?? null;
          }
        }

        setServerErrorDetails(serverErrorDetails);

        const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
        setError(error);
        setRequestDetails({
          url,
          method,
          body,
          response: {
            status: response.status,
            statusText: response.statusText,
          },
        });
      }

      return response;
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }

      // Prüfe, ob eine Internetverbindung besteht
      if (!navigator.onLine) {
        const offlineError = new Error('Keine Internetverbindung verfügbar. Bitte überprüfen Sie Ihre Netzwerkverbindung und versuchen Sie es erneut.');
        setServerErrorDetails(null);
        setError(offlineError);
        setRequestDetails({
          url,
          method,
          body,
          isOffline: true
        });
        throw offlineError;
      }

      setServerErrorDetails(null);
      setError(error as Error);
      setRequestDetails({
        url,
        method,
        body,
      });
      throw error;
    }
  };
};
